import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetMatkaBetsQuery, useCancelMatkaBetMutation, useCancelMatkaEventMutation } from '../services/matka/matkaApi';

const PAGE_SIZE = 20;

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusBadge = (status) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700',
    won:     'bg-green-100 text-green-700',
    lost:    'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded font-medium capitalize ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {status || '-'}
    </span>
  );
};

const MatkaAllBetsPage = () => {
  const { eventId, name } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const isAdmin = (() => {
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) return JSON.parse(userData).userType === 'Admin';
    } catch (_) {}
    return false;
  })();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, bet: null });
  const [eventCancelModal, setEventCancelModal] = useState(false);
  const [cancelMatkaBet, { isLoading: isCancelling }] = useCancelMatkaBetMutation();
  const [cancelMatkaEvent, { isLoading: isCancellingEvent }] = useCancelMatkaEventMutation();

  const handleCancelConfirm = async () => {
    if (!confirmModal.bet) return;
    try {
      await cancelMatkaBet(confirmModal.bet._id).unwrap();
      refetch();
    } catch (err) {
      alert(err?.data?.message || 'Failed to cancel bet');
    } finally {
      setConfirmModal({ isOpen: false, bet: null });
    }
  };

  const handleCancelEvent = async () => {
    try {
      await cancelMatkaEvent({ market, eventId }).unwrap();
      refetch();
    } catch (err) {
      alert(err?.data?.message || 'Failed to cancel event');
    } finally {
      setEventCancelModal(false);
    }
  };

  const market = eventId?.split('-').slice(3).join('-') || '';
  const { data, isLoading, error, refetch } = useGetMatkaBetsQuery(
    { market, eventId },
    { skip: !eventId }
  );

  const bets = data?.bets || [];
  const totalPages = Math.ceil(bets.length / PAGE_SIZE);
  const paginated = bets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const displayName = name ? decodeURIComponent(name) : eventId;

  return (
    <>
    <div className="w-full">
      <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
        <span className="text-white font-semibold text-base capitalize">
          All Bets - {displayName}
        </span>
        <div className="flex gap-2">
          <button
            onClick={refetch}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 text-sm transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-4 my-3 p-3 bg-red-100 text-red-700 border border-red-300 text-sm">
          {error?.data?.message || 'Failed to load bets'}
        </div>
      )}

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : (
          <>
            <table className="result-table w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white">
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">#</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Name</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Runner Name</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Bet Price</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Bet Amount</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Status</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Place Time</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Profit</th>
                  <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Loss</th>
                  {isAdmin && <th className="px-4 py-3 text-left font-medium border border-[#1a3a40]">Action</th>}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-gray-400 border border-gray-200">
                      No bets found
                    </td>
                  </tr>
                ) : (
                  paginated.map((bet, idx) => {
                    const profit = bet.profitLoss > 0 ? bet.profitLoss : (bet.adminProfit || 0);
                    const loss   = bet.profitLoss < 0 ? Math.abs(bet.profitLoss) : (bet.adminLoss || 0);
                    const s = (bet.status || '').toLowerCase();
                    const resultClass = (loss > 0 || s.includes('los')) ? 'bet-lost'
                      : (profit > 0 || s.includes('won') || s.includes('win')) ? 'bet-won'
                      : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50');
                    return (
                      <tr key={bet._id || idx} className={resultClass}>
                        <td className="px-4 py-2.5 border border-gray-200">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                        <td className="px-4 py-2.5 border border-gray-200">
                          <div>{bet.user?.fullName || bet.user?.username || bet.userId || '-'}</div>
                          {bet.userId && <div className="text-xs text-blue-500 font-semibold">{bet.userId}</div>}
                        </td>
                        <td className="px-4 py-2.5 border border-gray-200">{bet.runnerName || '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{bet.rate ?? '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{bet.stake ?? '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{statusBadge(bet.status)}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{formatTime(bet.placedAt)}</td>
                        <td className="px-4 py-2.5 border border-gray-200 text-green-600 font-medium">{profit > 0 ? profit.toFixed(2) : '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200 text-red-500 font-medium">{loss > 0 ? loss.toFixed(2) : '-'}</td>
                        {isAdmin && (
                          <td className="px-4 py-2.5 border border-gray-200">
                            <button
                              onClick={() => setConfirmModal({ isOpen: true, bet })}
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-xs font-medium transition-colors"
                            >
                              Cancel Bet
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex justify-end items-center gap-1 px-4 py-3 border-t border-gray-200 bg-white">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 text-gray-500 disabled:opacity-40 hover:bg-gray-50 text-sm"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-8 h-8 flex items-center justify-center border text-sm font-medium ${
                      currentPage === p
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 text-gray-500 disabled:opacity-40 hover:bg-gray-50 text-sm"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

      {/* Cancel Event Modal */}
      {eventCancelModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', backgroundColor: 'rgba(255,255,255,0.15)' }}
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Cancel Event</h3>
            <p className="text-gray-600 text-sm mb-5">
              Kya aap <span className="font-bold text-gray-800">{displayName}</span> ki saari bets cancel karna chahte hain?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEventCancelModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCancelEvent}
                disabled={isCancellingEvent}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white disabled:opacity-60"
              >
                {isCancellingEvent ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', backgroundColor: 'rgba(255,255,255,0.15)' }}
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Cancel Bet</h3>
            <p className="text-gray-600 text-sm mb-5">
              Kya aap <span className="font-bold text-gray-800">{confirmModal.bet?.userId || confirmModal.bet?.user?.userId}</span> ki bet <span className="font-bold text-gray-800">{confirmModal.bet?.runnerName}</span> cancel karna chahte hain?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmModal({ isOpen: false, bet: null })}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={isCancelling}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white disabled:opacity-60"
              >
                {isCancelling ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MatkaAllBetsPage;
